/*
 * Peppermint volume pricing table
 * - Renders the tier table from the snippet's data attributes
 * - Adds quantities already in the cart, read from an in-memory cache
 * - The cache is filled once per page load and refreshed only when the cart changes
 */
(() => {
  if (window.__peppermintVolumePricing) return;
  window.__peppermintVolumePricing = true;

  const selector = '.custom-volume-pricing';

  // true  = every variant of this product in the cart counts toward the tier
  // false = only the selected variant counts
  const MATCH_WHOLE_PRODUCT = true;

  /* ---------- helpers ---------- */

  const parseJson = (value, fallback) => {
    try {
      return JSON.parse(value || '');
    } catch {
      return fallback;
    }
  };

  const escapeHtml = (value) =>
    String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');

  // Prices are shown in the visitor's market currency. The snippet renders a sample amount
  // (1,234,567.00) with Shopify's own money format for that currency, e.g. "$1,234,567.00",
  // "1.234.567,00 €" or "CHF 1'234'567.00"; symbol, separators and decimals are copied from it.
  const getMoneyFormat = (root) => {
    if (root.__vpMoneyFormat !== undefined) return root.__vpMoneyFormat;
    const sample = (root.dataset.moneySample || '').trim();
    const match = sample.match(/1(\D*)234(\D*)567(?:(\D+)(\d+))?/);
    root.__vpMoneyFormat = match
      ? {
          prefix: sample.slice(0, match.index),
          suffix: sample.slice(match.index + match[0].length),
          thousands: match[1],
          decimal: match[3] || '',
          decimals: match[4] ? match[4].length : 0,
        }
      : null;
    return root.__vpMoneyFormat;
  };

  // Whole amounts without decimals ($21, not $21.00); otherwise always two decimals ($16.80)
  const formatMoney = (root, cents) => {
    const amount = Math.max(0, Math.round(cents));
    const whole = amount % 100 === 0;
    const format = getMoneyFormat(root);
    if (format) {
      const decimals = whole ? 0 : format.decimals;
      const fixed = (amount / 100).toFixed(decimals);
      const [integer, fraction] = fixed.split('.');
      const grouped = integer.replace(/\B(?=(\d{3})+(?!\d))/g, format.thousands);
      return `${format.prefix}${grouped}${fraction ? format.decimal + fraction : ''}${format.suffix}`;
    }
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: root.dataset.currency || 'EUR',
      minimumFractionDigits: whole ? 0 : 2,
      maximumFractionDigits: whole ? 0 : 2,
    }).format(amount / 100);
  };

  // Fixed-amount tiers are saved in the shop currency; convert them for other market currencies
  const getCurrencyRate = (root) => {
    const active = root.dataset.currency;
    const shopCurrency = root.dataset.shopCurrency;
    if (!active || !shopCurrency || active === shopCurrency) return 1;
    const rate = Number(window.Shopify && window.Shopify.currency && window.Shopify.currency.rate);
    return rate > 0 ? rate : 1;
  };

  const getScope = (root) => root.closest('.shopify-section') || root.closest('section') || document;

  // Only the quantity selector of the table's own section: tables inside product cards (e.g. a
  // featured collection on the product page) must not read or change the main product's quantity
  const getQuantityInput = (scope) => scope.querySelector('input[name="quantity"]');

  const getVariantInput = (scope) =>
    scope.querySelector('form[action*="/cart/add"] [name="id"]') ||
    scope.querySelector('[name="id"]') ||
    document.querySelector('form[action*="/cart/add"] [name="id"]');

  const getTierPrice = (root, basePrice, tier) => {
    const value = Number(tier.value || 0);
    if (tier.type === 'percentage') {
      return Math.max(0, Math.round(basePrice * (1 - value / 100)));
    }
    if (tier.type === 'amount' && tier.applies_each_item === true) {
      return Math.max(0, basePrice - Math.round(value * 100 * getCurrencyRate(root)));
    }
    return basePrice;
  };

  const getDiscountLabel = (root, tier) => {
    const value = Number(tier.value || 0);
    if (tier.type === 'percentage') return `-${value}%`;
    if (tier.type === 'amount' && tier.applies_each_item === true) {
      return `Save ${formatMoney(root, Math.round(value * 100 * getCurrencyRate(root)))}`;
    }
    return '';
  };

  /* ---------- cart cache ---------- */

  const CART_URL = `${(window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/'}cart.js`;
  const CART_MUTATION = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
  const nativeFetch = window.fetch.bind(window);

  let cartItems = [];
  let cartRequest = null;
  let cartDirty = false;

  const setCart = (cart) => {
    cartItems = Array.isArray(cart && cart.items) ? cart.items : [];
    renderAll();
  };

  const loadCart = () => {
    // One request at a time; if the cart changes mid-request, fetch once more afterwards
    if (cartRequest) {
      cartDirty = true;
      return;
    }
    cartRequest = nativeFetch(CART_URL, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    })
      .then((res) => (res.ok ? res.json() : Promise.reject(res)))
      .then(setCart)
      .catch(() => {})
      .finally(() => {
        cartRequest = null;
        if (cartDirty) {
          cartDirty = false;
          loadCart();
        }
      });
  };

  const getCartQuantity = (variantIds) =>
    cartItems.reduce(
      (sum, item) => (variantIds.has(String(item.variant_id)) ? sum + Number(item.quantity || 0) : sum),
      0
    );

  // Watch fetch-based cart changes (Dawn product form, cart drawer, apps)
  window.fetch = function (input, init) {
    const request = nativeFetch(input, init);
    const url = typeof input === 'string' ? input : (input && input.url) || String(input);

    if (CART_MUTATION.test(url)) {
      request
        .then((response) =>
          response
            .clone()
            .json()
            .then((data) => {
              // change / update / clear return the full cart: use it directly
              if (data && typeof data.item_count === 'number' && Array.isArray(data.items)) {
                if (cartRequest) cartDirty = true;
                setCart(data);
              } else {
                // add returns only the added item, so re-read the cart
                loadCart();
              }
            })
            .catch(loadCart)
        )
        .catch(() => {});
    }
    return request;
  };

  // Watch XHR-based cart changes (older apps)
  const nativeOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url, ...rest) {
    if (CART_MUTATION.test(String(url))) this.addEventListener('loadend', loadCart);
    return nativeOpen.call(this, method, url, ...rest);
  };

  /* ---------- render ---------- */

  // "€17.85", or with the "each" wording once the quantity is above 1, e.g. "at €17.85/ea".
  // The wording comes from price_at_each_html (merchant-edited HTML), the price stays bold.
  const getActivePriceHtml = (money, eachTemplate) => {
    const strong = `<strong class="custom-volume-pricing__discounted">${money}</strong>`;
    const at = eachTemplate ? eachTemplate.indexOf('[[price]]') : -1;
    if (at === -1) return strong;
    const before = eachTemplate.slice(0, at);
    const after = eachTemplate.slice(at + '[[price]]'.length);
    const grey = (html) => (html.trim() ? `<span class="custom-volume-pricing__each">${html}</span>` : '');
    return `<span class="custom-volume-pricing__price-each">${grey(before)}${strong}${grey(after)}</span>`;
  };

  const getEffectiveTiers = (root) => {
    // Sale discounts: a tier can carry a "sale_value" next to its regular "value". While the sale
    // runs (metafield "sale" with optional starts_at / ends_at), the sale value is what applies and
    // the regular discount is shown struck through. A tier with only a sale value (e.g. 50+ at 35%)
    // appears during the sale only.
    const sale = parseJson(root.dataset.sale, null);
    const now = Date.now();
    const saleActive =
      !sale ||
      ((!sale.starts_at || now >= Date.parse(sale.starts_at)) && (!sale.ends_at || now < Date.parse(sale.ends_at)));
    const hasSaleValue = (tier) =>
      tier.sale_value !== undefined && tier.sale_value !== null && tier.sale_value !== '' && Number(tier.sale_value) > 0;

    return parseJson(root.dataset.tiers, [])
      .map((tier) => {
        // on_sale (set by the app) switches a tier's sale discount on or off; no flag = follow the sale dates
        const onSale = saleActive && hasSaleValue(tier) && tier.on_sale !== false;
        return { ...tier, regular_value: Number(tier.value || 0), value: onSale ? Number(tier.sale_value) : Number(tier.value || 0), on_sale: onSale };
      })
      .filter((tier) => Number(tier.min_quantity) > 0 && tier.value > 0)
      .sort((a, b) => Number(a.min_quantity) - Number(b.min_quantity));
  };

  const renderVolumePricing = (root) => {
    const scope = getScope(root);
    const list = root.querySelector('ul');
    const currentPrice = root.querySelector('[data-volume-price]');
    if (!list || !currentPrice) return;

    const tiers = getEffectiveTiers(root);
    const variants = parseJson(root.dataset.variants, []);
    if (!tiers.length || !variants.length) return;

    const quantityInput = getQuantityInput(scope);
    const variantInput = getVariantInput(scope);
    const selectedVariantId = (variantInput && variantInput.value) || root.dataset.variantId;
    const variant = variants.find((item) => String(item.id) === String(selectedVariantId)) || variants[0];
    if (!variant) return;

    const inputQuantity = Math.max(1, Number((quantityInput && quantityInput.value) || 1));
    const matchIds = new Set(
      MATCH_WHOLE_PRODUCT ? variants.map((item) => String(item.id)) : [String(variant.id)]
    );
    const quantity = inputQuantity + getCartQuantity(matchIds);

    const basePrice = Number(variant.price || 0);
    const compareAtPrice = Number(variant.compare_at_price || 0);

    let activeTier = null;
    tiers.forEach((tier) => {
      if (quantity >= Number(tier.min_quantity)) activeTier = tier;
    });

    const activePrice = activeTier ? getTierPrice(root, basePrice, activeTier) : basePrice;
    const hasCompareAtPrice = compareAtPrice > basePrice;
    const originalPrice = hasCompareAtPrice ? compareAtPrice : basePrice;
    const shouldShowOriginal = hasCompareAtPrice || Boolean(activeTier && activePrice < basePrice);

    const pricePer = (root.dataset.pricePer || '').trim();
    const quoteLink = (root.dataset.quoteLink || '').trim();
    const quoteLinkText = root.dataset.quoteLinkText || '';

    // Lines switched off in the theme editor arrive as empty strings and are left out
    const tableHeading = root.dataset.tableHeading || '';
    const quantityHeading = root.dataset.quantityHeading || '';
    const discountHeading = root.dataset.discountHeading || '';
    const quoteLabel = root.dataset.quoteLabel || '';

    // Wording from Default theme content > Products > Volume pricing (Dawn's own texts)
    const minimumTemplate = root.dataset.minimumTemplate || '';
    const eachTemplate = root.dataset.eachTemplate || '';
    const minimumLabel = (count) =>
      minimumTemplate.includes('[[quantity]]') ? minimumTemplate.replace('[[quantity]]', count) : `${count}+`;

    const headingElement = root.querySelector('[data-volume-heading]');
    if (headingElement) {
      headingElement.textContent = tableHeading;
      headingElement.hidden = !tableHeading;
    }

    const priceHtml = `${
      shouldShowOriginal
        ? `<s class="custom-volume-pricing__original">${formatMoney(root, originalPrice)}</s>`
        : ''
    }${getActivePriceHtml(formatMoney(root, activePrice), inputQuantity > 1 ? eachTemplate : '')}`;

    const columnHeadingRow =
      quantityHeading || discountHeading
        ? `
      <li class="custom-volume-pricing__column-headings">
        <span>${escapeHtml(quantityHeading)}</span>
        <span>${escapeHtml(discountHeading)}</span>
      </li>`
        : '';

    // Struck-through first value, then the discount that applies now:
    // - sale running: regular tier discount struck, sale discount shown
    // - compare-at price: the tier's own discount struck, total discount vs. compare-at shown
    //   (rounded to a whole percent); with both, the regular discount is struck and the total shown
    const getTotalLabel = (tier) => {
      if (!hasCompareAtPrice) return '';
      const total = Math.round((1 - getTierPrice(root, basePrice, tier) / compareAtPrice) * 100);
      return total > 0 ? `-${total}%` : '';
    };

    const tierRows = tiers
      .map((tier) => {
        const label = getDiscountLabel(root, tier);
        const regularLabel =
          tier.on_sale && tier.regular_value > 0 ? getDiscountLabel(root, { ...tier, value: tier.regular_value }) : '';
        const shownLabel = getTotalLabel(tier) || label;
        let struckLabel = regularLabel || (hasCompareAtPrice ? label : '');
        if (struckLabel === shownLabel) struckLabel = '';
        // All discounts bold until a tier is reached; after that only the reached tier stays bold
        const bold = !activeTier || tier === activeTier;
        const shown = bold
          ? `<strong style="font-weight: 700 !important;">${escapeHtml(shownLabel)}</strong>`
          : escapeHtml(shownLabel);
        const discountHtml = struckLabel
          ? `<s class="custom-volume-pricing__tier-original">${escapeHtml(struckLabel)}</s> ${shown}`
          : shown;
        return `
      <li class="${tier === activeTier ? 'is-current' : ''}${tier.on_sale ? ' is-sale' : ''}" data-volume-tier
        data-min-quantity="${Number(tier.min_quantity)}" role="button" tabindex="0"
        aria-label="${escapeHtml(`${minimumLabel(tier.min_quantity)} ${shownLabel}`)}">
        <span>${escapeHtml(minimumLabel(tier.min_quantity))}${pricePer ? ` ${escapeHtml(pricePer)}` : ''}</span>
        <span>${discountHtml}</span>
      </li>`;
      })
      .join('');

    const quoteRow =
      quoteLabel || quoteLinkText
        ? `
      <li class="custom-volume-pricing__quote">
        ${quoteLabel ? `<span>${escapeHtml(quoteLabel)}</span>` : ''}
        ${
          quoteLinkText && quoteLink
            ? `<a href="${escapeHtml(quoteLink)}" aria-label="${escapeHtml(quoteLinkText)}">${escapeHtml(
                quoteLinkText
              )} <span aria-hidden="true">→</span></a>`
            : ''
        }
      </li>`
        : '';

    const listHtml = columnHeadingRow + tierRows + quoteRow;

    // Only touch the DOM when something actually changed
    if (root.__vpPrice !== priceHtml) {
      currentPrice.innerHTML = priceHtml;
      root.__vpPrice = priceHtml;
    }
    if (root.__vpList !== listHtml) {
      list.innerHTML = listHtml;
      root.__vpList = listHtml;
    }
  };

  function renderAll() {
    document.querySelectorAll(selector).forEach(renderVolumePricing);
  }

  /* ---------- triggers ---------- */

  // Quantity typing, +/- buttons (Dawn fires 'change' on the input), variant switches
  document.addEventListener('input', (event) => {
    if (event.target && event.target.name === 'quantity') renderAll();
  });

  document.addEventListener('change', (event) => {
    if (event.target && (event.target.name === 'quantity' || event.target.name === 'id')) renderAll();
  });

  document.addEventListener('click', (event) => {
    if (
      event.target.closest &&
      event.target.closest('quantity-input button, .quantity button, button[name="plus"], button[name="minus"]')
    ) {
      // Dawn updates the value in its own click handler; render right after it
      requestAnimationFrame(renderAll);
    }
  });

  // Puts a quantity into the selector, respecting its minimum, step and maximum
  const setQuantity = (input, quantity) => {
    const step = Math.max(1, Number(input.step) || 1);
    const min = Math.max(1, Number(input.min) || 1);
    let target = Math.max(min, Number(quantity) || 1);
    target = Math.ceil(target / step) * step;
    if (input.max && Number(input.max) > 0) target = Math.min(target, Number(input.max));

    input.value = target;
    // Dawn's quantity-input listens for 'change' (button states); our own listener re-renders
    input.dispatchEvent(new Event('change', { bubbles: true }));
  };

  // Clicking a tier puts that tier's starting quantity into the quantity selector.
  const selectTier = (row) => {
    const root = row.closest(selector);
    if (!root) return;
    const input = getQuantityInput(getScope(root));
    if (!input) return;
    input.__vpTouched = true;
    setQuantity(input, row.dataset.minQuantity);
  };

  document.addEventListener('click', (event) => {
    const row = event.target.closest && event.target.closest(`${selector} [data-volume-tier]`);
    if (row) selectTier(row);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    const row = event.target.closest && event.target.closest(`${selector} [data-volume-tier]`);
    if (row) {
      event.preventDefault();
      selectTier(row);
    }
  });

  const style = document.createElement('style');
  style.textContent = `
    ${selector} [data-volume-tier] { cursor: pointer; transition: background-color 0.15s ease; }
    ${selector} [data-volume-tier]:hover { background-color: rgba(0, 0, 0, 0.04); }
    ${selector} [data-volume-tier]:focus-visible { outline: 2px solid currentColor; outline-offset: 2px; }
  `;
  document.head.appendChild(style);

  // Dawn replaces the price block HTML on variant change. Re-render the fresh
  // copy before the browser paints so the unrendered version never flashes.
  new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node.nodeType === 1 && (node.matches(selector) || node.querySelector(selector))) {
          renderAll();
          return;
        }
      }
    }
  }).observe(document.documentElement, { childList: true, subtree: true });

  document.addEventListener('shopify:section:load', () => {
    document.querySelectorAll(selector).forEach(applyDefaultTier);
    renderAll();
  });

  ['cart:updated', 'cart:change', 'cart:refresh', 'cart-drawer:refresh'].forEach((name) =>
    document.addEventListener(name, loadCart)
  );

  window.addEventListener('pageshow', (event) => {
    if (event.persisted) loadCart();
  });

  // Default tier (is_default in the app): on page load the quantity selector starts at that
  // tier's quantity instead of 1. Only once per page, and only if the visitor hasn't changed
  // the quantity yet. A sale-only tier counts only while its sale discount is switched on.
  const applyDefaultTier = (root) => {
    if (root.__vpDefaultApplied) return;
    root.__vpDefaultApplied = true;
    const tier = getEffectiveTiers(root).find((item) => item.is_default === true || item.is_default === 'true');
    if (!tier) return;
    const input = getQuantityInput(getScope(root));
    if (!input || input.__vpTouched) return;
    if (Number(input.value) > Math.max(1, Number(input.min) || 1)) return;
    setQuantity(input, Number(tier.min_quantity));
  };

  // Remember when the visitor changes the quantity themselves (typing or +/-)
  document.addEventListener('input', (event) => {
    if (event.target && event.target.name === 'quantity') event.target.__vpTouched = true;
  });
  document.addEventListener('click', (event) => {
    const button = event.target.closest && event.target.closest('quantity-input button, .quantity button');
    const input = button && button.closest('quantity-input, .quantity')?.querySelector('input[name="quantity"]');
    if (input) input.__vpTouched = true;
  });

  // Dawn's product-info resets the quantity to the variant's minimum (usually 1) whenever another
  // variant (colour) is chosen. Keep the quantity the visitor had (e.g. the default tier) instead,
  // still within the new variant's minimum, maximum and step.
  customElements.whenDefined('product-info').then(() => {
    const ProductInfo = customElements.get('product-info');
    const original = ProductInfo && ProductInfo.prototype.setQuantityBoundries;
    if (!original || original.__vpKeepQuantity) return;
    const keepQuantity = function (...args) {
      const input = this.quantityInput || this.querySelector('input[name="quantity"]');
      const before = input ? Number(input.value) : 0;
      const result = original.apply(this, args);
      if (input && before > Number(input.value)) setQuantity(input, before);
      return result;
    };
    keepQuantity.__vpKeepQuantity = true;
    ProductInfo.prototype.setQuantityBoundries = keepQuantity;
  });

  const init = () => {
    document.querySelectorAll(selector).forEach(applyDefaultTier);
    renderAll(); // instant, from the input alone
    loadCart(); // then fold in the cart, once
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
